/**
 * Centralized cache key management with user isolation
 * All cache keys include user context to prevent data leakage
 * 
 * CRITICAL SECURITY FEATURE:
 * - Every cache key MUST include user ID to prevent cross-user data access
 * - Validates user ID format and presence
 * - Provides utilities for cache management and cleanup
 * - Supports bulk operations for user cache clearing
 */

export interface UserAwareCacheKeyOptions {
  userId: string;
  timezone?: string;
  includeTimestamp?: boolean;
}

export class UserAwareCacheKeys {
  /**
   * Validates user ID format and throws error if invalid
   * Auth0 user IDs typically follow format: "provider|id"
   */
  private static validateUserId(userId: string): void {
    if (!userId || typeof userId !== 'string' || userId.trim() === '') {
      throw new Error('[UserAwareCacheKeys] Invalid user ID for cache key generation');
    }
    
    // Additional validation for Auth0 format (optional but recommended)
    if (!userId.includes('|')) {
      console.warn('[UserAwareCacheKeys] User ID may not follow Auth0 format:', userId.substring(0, 20) + '...');
    }
  }

  // ========================
  // CORE DASHBOARD QUERIES
  // ========================
  
  static pnlTimeframes(userId: string, timezone: string): string[] {
    this.validateUserId(userId);
    return ['pnlTimeframes', userId, timezone];
  }

  static accountSummary(userId: string): string[] {
    this.validateUserId(userId);
    return ['accountSummary', userId];
  }

  static strategySummary(userId: string): string[] {
    this.validateUserId(userId);
    return ['strategySummary', userId];
  }

  static accountDaily(userId: string, timezone: string): string[] {
    this.validateUserId(userId);
    return ['accountDaily', userId, timezone];
  }

  static strategyDaily(userId: string, timezone: string): string[] {
    this.validateUserId(userId);
    return ['strategyDaily', userId, timezone];
  }

  static monthlyCalendar(userId: string, timezone: string): string[] {
    this.validateUserId(userId);
    return ['monthlyCalendar', userId, timezone];
  }

  // ========================
  // LIVE TRADES QUERIES
  // ========================
  
  static liveTrades(userId: string): string[] {
    this.validateUserId(userId);
    return ['liveTrades', userId];
  }

  static liveTradesWidget(userId: string): string[] {
    this.validateUserId(userId);
    return ['liveTradesWidget', userId];
  }

  // ========================
  // ACCOUNT & BALANCE QUERIES
  // ========================
  
  static accountBalances(userId: string): string[] {
    this.validateUserId(userId);
    return ['accountBalances', userId];
  }

  static accountBalancesDaily(userId: string, timezone: string, selectedAccounts?: string[]): string[] {
    this.validateUserId(userId);
    const accountsKey = selectedAccounts && selectedAccounts.length > 0 
      ? selectedAccounts.sort().join(',') // Sort for consistent cache keys
      : 'All';
    return ['accountBalancesDaily', userId, timezone, accountsKey];
  }

  // ========================
  // SETTINGS & USER DATA QUERIES
  // ========================
  
  static userSettings(userId: string): string[] {
    this.validateUserId(userId);
    return ['userSettings', userId];
  }

  static linkedAccounts(userId: string): string[] {
    this.validateUserId(userId);
    return ['linkedAccounts', userId];
  }

  static subscription(userId: string): string[] {
    this.validateUserId(userId);
    return ['subscription', userId];
  }

  // ========================
  // REPORTS & NOTIFICATIONS
  // ========================
  
  static reportPreferences(userId: string): string[] {
    this.validateUserId(userId);
    return ['reportPreferences', userId];
  }

  static reportHistory(userId: string, limit?: number, offset?: number): string[] {
    this.validateUserId(userId);
    return ['reportHistory', userId, String(limit || 10), String(offset || 0)];
  }

  // ========================
  // STRATEGY & BOT QUERIES
  // ========================
  
  static strategies(userId: string): string[] {
    this.validateUserId(userId);
    return ['strategies', userId];
  }

  static botRules(userId: string): string[] {
    this.validateUserId(userId);
    return ['botRules', userId];
  }

  // ========================
  // TRADE FILTERING QUERIES
  // ========================
  
  static tradeSymbols(userId: string, accountFilter?: string, strategyFilter?: string): string[] {
    this.validateUserId(userId);
    return ['tradeSymbols', userId, accountFilter || 'All', strategyFilter || 'All'];
  }

  static tradeAccounts(userId: string, symbolFilter?: string, strategyFilter?: string): string[] {
    this.validateUserId(userId);
    return ['tradeAccounts', userId, symbolFilter || 'All', strategyFilter || 'All'];
  }

  static tradeStrategies(userId: string, symbolFilter?: string, accountFilter?: string): string[] {
    this.validateUserId(userId);
    return ['tradeStrategies', userId, symbolFilter || 'All', accountFilter || 'All'];
  }

  // ========================
  // NOTIFICATION QUERIES
  // ========================
  
  static notifications(userId: string, options?: { limit?: number; offset?: number; unread_only?: boolean }): string[] {
    this.validateUserId(userId);
    const optionsKey = options ? JSON.stringify(options) : 'default';
    return ['notifications', userId, optionsKey];
  }

  static notificationUnreadCount(userId: string): string[] {
    this.validateUserId(userId);
    return ['notificationUnreadCount', userId];
  }

  // ========================
  // ONBOARDING & MAINTENANCE QUERIES
  // ========================
  
  static uncoveredTradesSummary(userId: string): string[] {
    this.validateUserId(userId);
    return ['uncoveredTradesSummary', userId];
  }

  static paginatedUncoveredTrades(userId: string, accountName: string, symbolName: string): string[] {
    this.validateUserId(userId);
    return ['paginatedUncoveredTrades', userId, accountName, symbolName];
  }

  static conflictsSummary(userId: string): string[] {
    this.validateUserId(userId);
    return ['conflictsSummary', userId];
  }

  // ========================
  // PORTFOLIO QUERIES
  // ========================
  
  static portfolioCategories(userId: string): string[] {
    this.validateUserId(userId);
    return ['portfolioCategories', userId];
  }

  static priorityActionsOptimized(userId: string, selectedStrategy?: string, selectedAccount?: string, forceRefresh?: number): string[] {
    this.validateUserId(userId);
    return ['priority-actions-optimized', userId, selectedStrategy || 'All', selectedAccount || 'All', String(forceRefresh || 0)];
  }

  static portfolioCategoryConfig(userId: string, category: string): string[] {
    this.validateUserId(userId);
    return ['portfolio-category-config', userId, category];
  }

  // ========================
  // UTILITY METHODS
  // ========================
  
  /**
   * Check if a cache key belongs to a specific user
   */
  static belongsToUser(queryKey: readonly unknown[], userId: string): boolean {
    if (!Array.isArray(queryKey) || queryKey.length < 2) {
      return false;
    }
    
    // The user ID should be the second element in all our cache keys
    return queryKey[1] === userId;
  }

  /**
   * Extract user ID from a cache key if present
   */
  static extractUserIdFromKey(queryKey: readonly unknown[]): string | null {
    if (!Array.isArray(queryKey) || queryKey.length < 2) {
      return null;
    }
    
    const userId = queryKey[1];
    return typeof userId === 'string' ? userId : null;
  }

  /**
   * Get all possible cache key prefixes for a user (for bulk operations)
   * This is used for clearing all cache data for a specific user
   */
  static getAllUserCacheKeyPrefixes(userId: string): string[][] {
    this.validateUserId(userId);
    
    return [
      // Core dashboard queries
      ['pnlTimeframes', userId],
      ['accountSummary', userId],
      ['strategySummary', userId],
      ['accountDaily', userId],
      ['strategyDaily', userId],
      ['monthlyCalendar', userId],
      
      // Live trades
      ['liveTrades', userId],
      ['liveTradesWidget', userId],
      
      // Account & balances
      ['accountBalances', userId],
      ['accountBalancesDaily', userId],
      
      // Settings & user data
      ['userSettings', userId],
      ['linkedAccounts', userId],
      ['subscription', userId],
      
      // Reports & Notifications
      ['reportPreferences', userId],
      ['reportHistory', userId],
      
      // Strategies & bots
      ['strategies', userId],
      ['botRules', userId],
      
      // Trade filtering
      ['tradeSymbols', userId],
      ['tradeAccounts', userId],
      ['tradeStrategies', userId],
      
      // Notifications
      ['notifications', userId],
      ['notificationUnreadCount', userId],
      
      // Onboarding & maintenance
      ['uncoveredTradesSummary', userId],
      ['paginatedUncoveredTrades', userId],
      ['conflictsSummary', userId],
      
      // Portfolio
      ['portfolioCategories', userId],
      ['priority-actions-optimized', userId],
      ['portfolio-category-config', userId],
    ];
  }

  /**
   * Check if a query key is a legacy (non-user-aware) key that needs migration
   */
  static isLegacyCacheKey(queryKey: readonly unknown[]): boolean {
    if (!Array.isArray(queryKey) || queryKey.length === 0) {
      return false;
    }

    const firstKey = queryKey[0];
    const knownQueryTypes = [
      'pnlTimeframes', 'accountSummary', 'strategySummary', 'accountDaily', 'strategyDaily',
      'monthlyCalendar', 'liveTrades', 'liveTradesWidget', 'accountBalances', 
      'accountBalancesDaily', 'userSettings', 'linkedAccounts', 'subscription',
      'strategies', 'botRules', 'tradeSymbols', 'tradeAccounts', 'tradeStrategies',
      'notifications', 'notificationUnreadCount', 'uncoveredTradesSummary',
      'paginatedUncoveredTrades', 'conflictsSummary', 'portfolioCategories',
      'priority-actions-optimized', 'portfolio-category-config'
    ];

    // If the first key matches a known query type but there's no user ID in position 1,
    // or the user ID doesn't look like an Auth0 user ID, it's legacy
    if (typeof firstKey === 'string' && knownQueryTypes.includes(firstKey)) {
      if (queryKey.length < 2) return true;
      
      const potentialUserId = queryKey[1];
      if (typeof potentialUserId !== 'string') return true;
      
      // Check if it looks like an Auth0 user ID (contains '|' character)
      if (!potentialUserId.includes('|')) return true;
    }

    return false;
  }

  /**
   * Check if a query key represents global/non-user-specific data
   */
  static isGlobalCacheKey(queryKey: readonly unknown[]): boolean {
    if (!Array.isArray(queryKey) || queryKey.length === 0) {
      return false;
    }

    const firstKey = queryKey[0];
    const globalKeys = [
      'featureFlags', 'systemHealth', 'marketData', 'globalConfig',
      'admin-feature-flags', 'publicData'
    ];

    return typeof firstKey === 'string' && globalKeys.includes(firstKey);
  }

  /**
   * Generate a debug summary of cache keys for troubleshooting
   */
  static generateDebugSummary(allQueryKeys: readonly (readonly unknown[])[]): {
    userAware: number;
    legacy: number;
    global: number;
    unknown: number;
    userBreakdown: { [userId: string]: number };
  } {
    const summary = {
      userAware: 0,
      legacy: 0,
      global: 0,
      unknown: 0,
      userBreakdown: {} as { [userId: string]: number }
    };

    allQueryKeys.forEach(queryKey => {
      if (this.isGlobalCacheKey(queryKey)) {
        summary.global++;
      } else if (this.isLegacyCacheKey(queryKey)) {
        summary.legacy++;
      } else {
        const userId = this.extractUserIdFromKey(queryKey);
        if (userId) {
          summary.userAware++;
          summary.userBreakdown[userId] = (summary.userBreakdown[userId] || 0) + 1;
        } else {
          summary.unknown++;
        }
      }
    });

    return summary;
  }
}